// libs
import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
// components
import { Box } from '@lyearn/ui';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import { useAppConfig } from '@/hooks/useAppConfig';

import CertificateCondition from './CertificateCondition';
// types
import { CertificateSettingsOptions, CertificateSettingsProps } from './types';
// utils
import { getCertificateSettingsItems } from './utils';

function CertificateSettings({
  certificateSettings,
  contentType,
  error,
  showConditionSettings = true,
  onChange,
  disabled,
}: CertificateSettingsProps) {
  const { t } = useTranslation();
  const { site } = useAppConfig();
  const { disableLinkedInSharing, disableSocialSharing } = site.certificateSharingSettings;

  const items = getCertificateSettingsItems({ t, disableLinkedInSharing, disableSocialSharing });

  const _onChange = useCallback(
    (
      id: Exclude<CertificateSettingsOptions, CertificateSettingsOptions.MinPercentage>,
      value: boolean,
    ) => {
      const certificateSettingsCopy = { ...certificateSettings };
      certificateSettingsCopy[id] = value;
      onChange(certificateSettingsCopy);
    },
    [certificateSettings, onChange],
  );

  const onConditionChange = useCallback(
    (value?: number) => {
      const certificateSettingsCopy = { ...certificateSettings };
      certificateSettingsCopy[CertificateSettingsOptions.MinPercentage] = value;
      onChange(certificateSettingsCopy);
    },
    [certificateSettings, onChange],
  );

  return (
    <Box className="mt-32 space-y-32">
      {showConditionSettings ? (
        <CertificateCondition
          contentType={contentType}
          error={error}
          minPercentage={certificateSettings.minPercentage}
          onChange={onConditionChange}
        />
      ) : null}
      {items.map(({ id, label, description }) => (
        <SwitchWithInfo
          key={id}
          checked={certificateSettings[id]}
          description={description}
          disabled={disabled}
          label={label}
          onChange={(value) => _onChange(id, value)}
        />
      ))}
    </Box>
  );
}

export default React.memo(CertificateSettings);
